import { useEffect } from 'react'
import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider'
import {
  OpenFeature,
  InMemoryProvider,
  ProviderStatus,
  ClientProviderEvents,
} from '@openfeature/react-sdk'
import { LDLogger } from 'launchdarkly-js-client-sdk'

import { isProduction } from '@/utils/envHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  getCurrentUser,
  selectFlagMembershipDetails,
} from '@/selectors/user.selectors'
import { logHeardError } from '@/utils/errorHelpers'
import type { ReduxState } from '@/utils/typeHelpers'

const logger: LDLogger = {
  debug: (_: string) => {},
  info: (_: string) => {},
  warn: (message: string) => logHeardError(message, { level: 'warning' }),
  error: (message: string) => logHeardError(message, { level: 'error' }),
}

export const OPEN_FEATURE_DOMAIN_NAME = 'openFeatureClient'

export const initOpenFeature = () => {
  const ldClientSideId = process.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID

  if (ldClientSideId) {
    const ldOpenFeatureProvider = new LaunchDarklyClientProvider(
      ldClientSideId,
      {
        streaming: true,
        logger: isProduction() ? logger : console,
      }
    )
    return OpenFeature.setProviderAndWait(
      OPEN_FEATURE_DOMAIN_NAME,
      ldOpenFeatureProvider
    )
  } else {
    return OpenFeature.setProviderAndWait(new InMemoryProvider({}))
  }
}

export const buildUserEvaluationContext = (
  user: { uuid: string; email: string; createdAt: string },
  bankingStage: string | null,
  membershipDetails: { membershipTier?: string; isTrialing?: boolean } = {}
) => ({
  kind: 'user' as const,
  targetingKey: user.uuid,
  email: user.email.toLowerCase(),
  createdAt: user.createdAt,
  ...membershipDetails,
  ...(bankingStage ? { bankingStage } : {}),
})

export const useSetOpenFeatureTargetingKey = () => {
  const user = useReselector(getCurrentUser)
  const analyticsStage = useReselector(
    (state: ReduxState) => state.banking.profile?.analyticsStage ?? null
  )
  const membershipDetails = useReselector(selectFlagMembershipDetails)

  useEffect(() => {
    if (user?.uuid) {
      OpenFeature.setContext(
        buildUserEvaluationContext(
          {
            uuid: user.uuid,
            email: user.email,
            createdAt: user.createdAt,
          },
          analyticsStage,
          membershipDetails
        )
      )
    }
  }, [
    user?.email,
    user?.uuid,
    user?.createdAt,
    analyticsStage,
    membershipDetails,
  ])
}

export const clearOpenFeatureTargetingKey = async () =>
  await OpenFeature.clearContext()

export enum HISTORICAL_BOOKKEEPING_FEE_COHORTS {
  cohort1 = 'COHORT_1',
  cohort2 = 'COHORT_2',
  none = 'NONE',
}

export const getReadyOpenFeatureClient = async () => {
  const client = OpenFeature.getClient(OPEN_FEATURE_DOMAIN_NAME)

  // If the client is in a NOT_READY state add handler to listen to readiness
  if (client.providerStatus === ProviderStatus.NOT_READY) {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('open feature not ready within 5 secs')),
        5000
      )

      client.addHandler(ClientProviderEvents.Ready, () => {
        clearTimeout(timeout)
        resolve()
      })
    })
  }

  // If client is reconciling then it is in the process of updating targeting, wait for this to complete
  if (client.providerStatus === ProviderStatus.RECONCILING) {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('open feature not ready within 5 secs')),
        5000
      )

      client.addHandler(ClientProviderEvents.ContextChanged, () => {
        clearTimeout(timeout)
        resolve()
      })
    })
  }

  return client
}

// When adding a new value, be sure to also add to heard-api/src/api/integrations/open_feature/config.ts
// Please keep in alphabetical order!!
export enum FEATURE_FLAG_KEYS {
  allowEditReleasedUserTaxEstimates = 'allow-edit-released-user-tax-estimates',
  alwaysOnQteEnabled = 'always-on-qte-enabled',
  canAccessTaxSeasonKickoffSurvey = 'can-access-tax-season-kickoff-survey',
  cancellationSaveFlowStaticValues = 'cancellation-save-flow-static-values',
  catchupBookkeepingFee = 'historical-bookkeeping-fee',
  dedicatedSupportPlans = 'dedicated-support-plans',
  disable2024AnnualTax = 'disable-2024-annual-tax',
  enable2024OptOutFlow = 'enable-2024-opt-out-flow',
  enableCancellationDiscounts = 'enable-cancellation-discounts',
  enableDeclaredReconciliationActions = 'enable-declared-reconciliation-actions',
  enableDesignSystem2 = 'enable-design-system-2',
  enableExtensionsOptIn = 'enable-extensions-opt-in',
  enableFreeTrial2026 = 'enable-free-trial-2026',
  enableFullLaunchTaxSeason = 'enable-full-launch-tax-season',
  enableHavenCubk = 'enable-haven-cubk',
  enableIncomePlanning = 'enable-income-planning',
  enableStarterPlans = 'enable-starter-plans',
  enableStripePlanIncidentMitigation = 'incident-2025-07-28',
  enableTaxConsultations = '2024-enable-tax-consultations',
  enableTaxDeadlineBanner = 'enable-tax-season-deadline-banner',
  featureAnnouncement = 'feature-announcement',
  financialAdvisoryPilot = 'financial-advisory-pilot',
  havenHandToSupport = 'haven-hand-to-support',
  havenMessagesView = 'haven-messages-view',
  maintenanceMessage = 'maintenance-message',
  newCatalogSignup = 'new-catalog-signup',
  optIntoTaxesSidebarEnabled = 'opt-into-taxes-sidebar-enabled',
  plaidIssuesAlert = 'plaid-issues-alert',
  planChangePromoCodes = 'plan-change-promo-codes',
  planCopyFromTier = 'plan-copy-from-tier',
  postTaxYearEnabled = 'post-tax-year-enabled',
  presortEnabled = 'presort-enabled',
  qteMultiStateCalc = 'qte-multi-state-calc',
  qteOpsRecalcEntry = 'qte-ops-recalc-entry',
  referralExperienceEnabled = 'referral-experience-enabled',
  replaceTherapistVariable = 'replace-therapist-variable',
  showLiveOnboardingBooking = 'show-live-onboarding-booking',
  showOnboardingSurveys = 'show-onboarding-surveys',
  sidebarNavAnnouncement = 'sidebar-nav-announcement',
  signUpPromos = 'sign-up-promos',
  signupBankingBonus = 'signup-banking-bonus',
  stytchAdminAuthEnabled = 'stytch-admin-auth-enabled',
  taxPackage2026Launch = 'tax-package-2026-launch',
  tosUpdateSignInBanner = 'tos-update-sign-in-banner',
  waiveCubkForFreeTrial2026 = 'waive-cubk-for-free-trial-2026',
}
